import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { company, gallery } from "../mock";
import Reveal from "../components/Reveal";
import StickyServices from "../components/StickyServices";
import Seo from "../components/Seo";

const PageHero = ({ eyebrow, title, subtitle }) => (
  <section className="pt-32 lg:pt-40 pb-4">
    <div className="max-w-6xl mx-auto px-5 lg:px-6">
      <span className="text-[#ea731d] font-semibold tracking-[0.2em] text-xs uppercase">{eyebrow}</span>
      <h1 className="mt-3 text-4xl lg:text-6xl font-bold text-[#1a1714] leading-[1.05] max-w-3xl">{title}</h1>
      {subtitle && <p className="mt-5 text-lg text-neutral-600 max-w-2xl">{subtitle}</p>}
    </div>
  </section>
);

const Services = () => {
  return (
    <div className="overflow-hidden">
      <Seo
        title="Cleaning Services in Manchester | iClean NW"
        description="Explore iClean NW's cleaning services: ongoing contract cleaning for nurseries and schools, commercial & office cleaning, end of tenancy & post-construction cleans, and grounds maintenance across the North West."
        path="/services"
      />
      <PageHero
        eyebrow="Our Services"
        title="Everything your premises needs, under one roof"
        subtitle="All our staff are vetted and trained in health & safety before placement. Explore what we offer below."
      />

      {/* STICKY STORYTELLING SERVICES */}
      <section className="pt-8 pb-2">
        <div className="max-w-3xl mx-auto px-5 lg:px-6">
          <p className="text-neutral-600 leading-relaxed">
            iClean NW delivers professional cleaning across Manchester and the wider North West, covering commercial, office and industrial premises as well as residential end-of-tenancy work. From daily contract cleaning for nurseries, schools and communal areas, to spotless offices, shops and warehouses, our vetted and fully-trained team maintains the highest standards on every visit. We also handle end-of-tenancy and post-construction cleans for houses, flats and commercial units, and keep the outside of your premises pristine with grounds maintenance including litter picking, bin-area cleaning and window cleaning. Whether you need a one-off deep clean or a long-term service contract, we build a tailored plan around your schedule and budget.
          </p>
        </div>
      </section>
      <StickyServices />

      {/* GALLERY */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <Reveal className="max-w-2xl">
            <span className="text-[#ea731d] font-semibold tracking-widest text-xs uppercase">Our work</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1a1714]">Results that speak for themselves</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {gallery.map((g, i) => (
              <Reveal key={i} delay={(i % 4) * 70} className={`overflow-hidden rounded-[22px] ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                <img src={g} alt={`iClean work ${i + 1}`} className={`w-full object-cover hover:scale-105 transition-transform duration-500 ${i === 0 ? "h-full min-h-[220px] lg:min-h-[420px]" : "h-48 lg:h-52"}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-6">
          <div className="rounded-[28px] bg-[#1a1714] px-8 py-12 lg:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white">Not sure which service you need?</h2>
              <p className="mt-2 text-neutral-400">Tell us about your premises and we’ll build a plan around you.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] text-white font-semibold px-7 py-3.5 rounded-full transition-colors">Get a quote</Link>
              <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/25 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/20 transition-colors"><Phone size={17} /> Call us</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
